import React, { useMemo } from "react";
import { cn } from "@/lib/utils";
import { ago } from "@/lib/format";
import { Panel, ProvenanceTag } from "@/components/common/primitives";
import { CopyValueButton } from "@/components/common/CopyValueButton";
import { groupProductDetails } from "@/lib/analyzerData";
import { WalmartAttributesPanel, useWalmartAttributes } from "@/components/analyzer/WalmartAttributesPanel";
import { ExternalLink } from "lucide-react";

const SECTION_TITLES = {
  identity: "Identity",
  catalog: "Catalog",
  packaging: "Package & Units",
  fulfillment: "Fulfillment & Compliance",
  tracking: "Source & Tracking",
};

// Labels that should NOT get a copy control (statuses / dates / confidence).
const NO_COPY = ["rating", "wfs", "hazmat", "oversize", "featured offer is", "restricted", "brand restriction", "tracking", "since", "confidence", "eligib"];
const copyable = (label) => !NO_COPY.some((k) => label.toLowerCase().includes(k));

function DetailRow({ label, value, rawCopyValue, mono }) {
  const showCopy = rawCopyValue !== undefined;
  return (
    <div className="flex items-center justify-between gap-2 text-compact py-1.5 border-b border-edge-subtle">
      <span className="text-ink-secondary min-w-0 truncate">{label}</span>
      <span className="flex items-center gap-1 min-w-0 justify-end">
        <span className={cn("text-ink-primary truncate", mono && "ident text-micro")} title={typeof value === "string" ? value : undefined}>{value ?? "—"}</span>
        {showCopy && <CopyValueButton value={rawCopyValue} label={label} />}
      </span>
    </div>
  );
}

function DetailGroup({ id, rows }) {
  if (!rows.length) return null;
  return (
    <Panel className="p-4" testid={`details-${id}`}>
      <div className="flex items-center justify-between mb-2"><span className="text-stitle text-ink-primary">{SECTION_TITLES[id]}</span><ProvenanceTag kind="fixture" /></div>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-0.5">
        {rows.map((d, i) => (
          <DetailRow key={i} label={d.label} value={d.value} mono={d.mono}
            rawCopyValue={copyable(d.label) ? d.value : undefined} />
        ))}
      </div>
    </Panel>
  );
}

function ChildItemIds({ ids }) {
  const validIds = useMemo(() => Array.from(new Set((ids || []).map((x) => String(x).trim()).filter(Boolean))), [ids]);
  const allValue = validIds.length ? validIds.join("\n") : null;
  return (
    <div className="mt-1">
      <div className="flex items-center justify-between gap-2 text-compact py-1.5 border-b border-edge-subtle">
        <span className="text-ink-secondary">Child Walmart Item IDs</span>
        <span className="flex items-center gap-1"><span className="text-ink-muted text-micro">{validIds.length} tracked</span>
          <CopyValueButton value={allValue} label="all child Walmart Item IDs" /></span>
      </div>
      {validIds.length === 0 ? <span className="text-ink-muted text-compact">—</span> : (
        <ul className="mt-1 space-y-0.5" data-testid="child-item-id-list">
          {validIds.map((id) => (
            <li key={id} className="flex items-center justify-between gap-2 text-compact py-1 pl-3">
              <span className="ident text-micro text-ink-primary truncate" title={id}>{id}</span>
              <CopyValueButton value={id} label={`child Walmart Item ID ${id}`} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function UrlRow({ label, url }) {
  return (
    <div className="flex items-center justify-between gap-2 text-compact py-1.5 border-b border-edge-subtle">
      <span className="text-ink-secondary">{label}</span>
      <span className="flex items-center gap-1 min-w-0 justify-end">
        {url ? (
          <>
            <a href={url} target="_blank" rel="noreferrer noopener" onClick={(e) => e.stopPropagation()} title={url}
              className="inline-flex items-center gap-1 text-signal-blue hover:underline truncate ident text-micro">
              {url.replace(/^https?:\/\//, "")}<ExternalLink className="h-3 w-3 shrink-0" />
            </a>
            <CopyValueButton value={url} label={label} />
          </>
        ) : <span className="text-ink-muted">—</span>}
      </span>
    </div>
  );
}

export function WalmartProductDetailsTab({ bundle, product, liveMode = false }) {
  const groups = groupProductDetails(bundle?.product_details || []);
  const variations = bundle?.variations || [];
  const parentId = product?.walmart?.item_id_clean || product?.walmart?.walmart_item_id || null;
  const parentClean = parentId ? String(parentId).replace(/[^0-9A-Za-z]/g, "") : "";
  const walmartUrl = product?.walmart?.url || (parentClean ? `https://www.walmart.com/ip/${parentClean}` : null);
  const notes = liveMode ? null : bundle?.notes_tags;
  const hasFixtureDetails = !liveMode && (bundle?.product_details || []).length > 0;
  const unwrangleDetails = liveMode && (bundle?.product_details || []).length > 0;
  const unwrangleGroups = unwrangleDetails ? groups : null;

  // Attributes IziSell already holds for this item (cache read — no provider call).
  const { attributes, loading: attributesLoading } = useWalmartAttributes(parentClean);

  // Live without Unwrangle detail rows: fall back to product shell (Marketplace).
  const liveIdentity = liveMode && !unwrangleDetails
    ? [
        { label: "Walmart Item ID", value: parentClean || "—", mono: true },
        { label: "UPC", value: product?.upc || product?.walmart?.upc || "—", mono: true },
        { label: "GTIN", value: product?.walmart?.gtin || "—", mono: true },
        { label: "Brand", value: product?.walmart?.brand || product?.source?.brand || "—" },
        { label: "Title", value: product?.walmart?.title || "—" },
      ]
    : null;
  const liveCatalog = liveMode && !unwrangleDetails
    ? [
        { label: "Category", value: product?.category || product?.walmart?.category || "—" },
        { label: "Product type", value: product?.walmart?.productType || "—" },
        { label: "Price (catalog)", value: product?.walmart?.price != null ? `$${Number(product.walmart.price).toFixed(2)}` : "—" },
        { label: "Rating", value: product?.walmart?.rating != null ? String(product.walmart.rating) : "—" },
        { label: "Reviews", value: product?.walmart?.reviews != null ? String(product.walmart.reviews) : "—" },
      ]
    : null;

  return (
    <section data-testid="walmart-product-details-tab" className="space-y-4">
      <header>
        <h2 className="text-stitle text-ink-primary">Product Details</h2>
        <p className="text-compact text-ink-secondary">
          {liveMode
            ? unwrangleDetails
              ? "Identity and specs from live PDP scrape (Oxylabs or Unwrangle). Missing attributes stay empty — never backfilled from a different demo product."
              : "Fields from the live catalog response. Missing attributes stay empty — never backfilled from a different demo product."
            : "Catalog identity, packaging, fulfillment classification, compliance, and source — no profit or seller analytics here."}
        </p>
      </header>

      <WalmartAttributesPanel attributes={attributes} loading={attributesLoading} />

      {liveMode && unwrangleDetails ? (
        <>
          <Panel className="p-4" testid="details-identity">
            <div className="flex items-center justify-between mb-2">
              <span className="text-stitle text-ink-primary">Identity</span>
              <span className="text-micro text-ink-muted font-mono">
                {bundle?.source === "oxylabs" ? "oxylabs" : "unwrangle"}
              </span>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-0.5">
              {(unwrangleGroups.identity || []).map((d, i) => (
                <DetailRow key={i} label={d.label} value={d.value} mono={d.mono}
                  rawCopyValue={copyable(d.label) && d.value && d.value !== "—" ? d.value : undefined} />
              ))}
              {(unwrangleGroups.catalog || []).map((d, i) => (
                <DetailRow key={`c-${i}`} label={d.label} value={d.value} mono={d.mono}
                  rawCopyValue={copyable(d.label) && d.value && d.value !== "—" ? d.value : undefined} />
              ))}
              <UrlRow label="Walmart product URL" url={walmartUrl} />
            </div>
          </Panel>
          {(unwrangleGroups.packaging || []).length > 0 && (
            <DetailGroup id="packaging" rows={unwrangleGroups.packaging} />
          )}
          {(variations || []).length > 0 && (
            <Panel className="p-4" testid="details-variation-family">
              <div className="flex items-center justify-between mb-2">
                <span className="text-stitle text-ink-primary">Variation Family</span>
                <span className="text-micro text-ink-muted font-mono">
                  {bundle?.source === "oxylabs" ? "oxylabs" : "unwrangle"}
                </span>
              </div>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-0.5">
                <DetailRow label="Parent Walmart Item ID" value={parentId || "—"} rawCopyValue={parentClean || null} mono />
                <DetailRow label="Tracked variations" value={String(variations.length)} />
              </div>
              <ChildItemIds ids={variations.map((v) => v.item_id)} />
            </Panel>
          )}
        </>
      ) : liveMode ? (
        <>
          <Panel className="p-4" testid="details-identity">
            <div className="flex items-center justify-between mb-2">
              <span className="text-stitle text-ink-primary">Identity</span>
              <span className="text-micro text-ink-muted font-mono">walmart_catalog</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-0.5">
              {liveIdentity.map((d, i) => (
                <DetailRow key={i} label={d.label} value={d.value} mono={d.mono}
                  rawCopyValue={copyable(d.label) && d.value && d.value !== "—" ? d.value : undefined} />
              ))}
            </div>
          </Panel>
          <Panel className="p-4" testid="details-catalog">
            <div className="flex items-center justify-between mb-2">
              <span className="text-stitle text-ink-primary">Catalog</span>
              <span className="text-micro text-ink-muted font-mono">walmart_catalog</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-0.5">
              {liveCatalog.map((d, i) => (
                <DetailRow key={i} label={d.label} value={d.value} />
              ))}
              <UrlRow label="Walmart product URL" url={walmartUrl} />
            </div>
          </Panel>
          <Panel className="p-4" testid="details-unavailable-extra">
            <UnavailableNote
              title="Packaging, fulfillment flags, and variation trees"
              reason="product_details_unavailable"
              message="Not returned by Marketplace catalog search for this item."
            />
          </Panel>
        </>
      ) : (
        <>
          <DetailGroup id="identity" rows={groups.identity} />
          <DetailGroup id="catalog" rows={groups.catalog} />
          <DetailGroup id="packaging" rows={groups.packaging} />
          <DetailGroup id="fulfillment" rows={groups.fulfillment} />

          <Panel className="p-4" testid="details-variation-family">
            <div className="flex items-center justify-between mb-2"><span className="text-stitle text-ink-primary">Variation Family</span><ProvenanceTag kind="fixture" /></div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-0.5">
              <DetailRow label="Parent Walmart Item ID" value={parentId || "—"} rawCopyValue={parentClean || null} mono />
              <DetailRow label="Tracked variations" value={String(variations.length)} />
              <DetailRow label="Variation attributes" value="Size · Pack count" />
            </div>
            <ChildItemIds ids={variations.map((v) => v.item_id)} />
          </Panel>

          <Panel className="p-4" testid="details-tracking">
            <div className="flex items-center justify-between mb-2"><span className="text-stitle text-ink-primary">Source & Tracking</span><ProvenanceTag kind="fixture" /></div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-0.5">
              {groups.tracking.map((d, i) => <DetailRow key={i} label={d.label} value={d.value} />)}
              <UrlRow label="Walmart product URL" url={walmartUrl} />
              <UrlRow label="Source-store URL" url={null} />
              <DetailRow label="Data provider" value="Ezisell demo" rawCopyValue="Ezisell demo" />
            </div>
          </Panel>
        </>
      )}

      {notes && (
        <Panel className="p-4" testid="details-notes">
          <div className="text-stitle text-ink-primary mb-2">Notes & Tags</div>
          <textarea defaultValue={notes.notes} aria-label="Notes" className="w-full h-20 rounded-ctrl border border-edge-subtle p-2 text-compact resize-none focus:outline-none focus:ring-2 focus:ring-signal-blue/40" />
          <div className="flex flex-wrap gap-1.5 mt-2">{notes.tags.map((t) => <span key={t} className="text-micro bg-surface-2 rounded px-1.5 py-0.5">{t}</span>)}</div>
          <div className="mt-3 space-y-1">{notes.activity.map((a, i) => <div key={i} className="text-micro text-ink-muted">{ago(a.at)} · {a.actor} — {a.action}</div>)}</div>
        </Panel>
      )}
      {!liveMode && !hasFixtureDetails && (
        <UnavailableNote title="No product details" reason="empty" message="No details payload for this view." />
      )}
    </section>
  );
}

function UnavailableNote({ title, message, reason }) {
  return (
    <div className="py-6 text-center" data-testid="details-section-unavailable">
      <div className="text-compact font-medium text-ink-primary">{title}</div>
      {message && <p className="text-compact text-ink-secondary mt-1">{message}</p>}
      {reason && <p className="text-micro font-mono text-ink-muted mt-2">{reason}</p>}
    </div>
  );
}

export default WalmartProductDetailsTab;
